import { useState, useContext, useEffect } from "react";
import { StateContextType } from "../../../types/state.types";
import MainStateContext from "../../../state/mainStateContext";
import usePhotoModerationUpload from "../../../usePhotoModeration/usePhotoModerationUpload";
import SelfieButton from "../photoModeration/SelfieButton";
import SelfieModerationSteps from "../photoModeration/SelfieModerationSteps";
import {
  triggerAnalyticsEvent,
  updateOrderDetailLocalStorage,
  scrollToApp
} from "../../../helpers";
import { requestAddPhotoApi } from "../../../services/addPhotoToOrderAPI";
import SubmitMessage from "../../Shared/SubmitMessage";
import PhotoLoad from "../photoModeration/PhotoLoad";
import HorizontalTextLine from "../../Shared/HorizontalTextLine";
import Group from "../../Shared/Group";
import OrderConfirmationSelfieOnlyText from "./OrderConfirmationSelfieOnlyText";
import { getPhrase, Say } from "../../../languages";
import { PhotoUploadPayload } from "../../../types";

const OrderConfirmationPhotoUpload = ({ currentOrder }) => {
  const { state, dispatch, toggleOverlay, setSubmitMessagePhraseKey } = useContext(
    MainStateContext
  ) as StateContextType;
  const { eventData, moderatedSelfieData, submitMessagePhraseKey } = state;
  const { orderId, orderRef } = currentOrder;
  const { PhotoType = "" }: { PhotoType?: string } = eventData;
  const photoType = PhotoType?.toLowerCase();
  const [isServiceError, setIsServiceError] = useState(false);
  const [photoModerationStep, setPhotoModerationStep] = useState("select_photo");
  const {
    base64Photo,
    handleFileChange,
    photoModerationError,
    photoDenyReasons,
    photoModerationLoading
  } = usePhotoModerationUpload();

  useEffect(() => {
    if (photoType === "booth") return;
    if (photoDenyReasons) {
      const rejectReasonsString = photoDenyReasons?.join(", ");

      setIsServiceError(false);

      const errorMessageText = getPhrase("photo_step.photo_reject_message");
      triggerAnalyticsEvent("photo_moderation_error", {
        eventLabel: `${errorMessageText}:${rejectReasonsString}`,
        errorMessage: `${errorMessageText}:${rejectReasonsString}`
      });
    }
  }, [photoDenyReasons]);

  useEffect(() => {
    if (photoType === "booth") return;
    if (photoModerationError) {
      setIsServiceError(true);
      setPhotoModerationStep("selfie_rejected");
    }
  }, [photoModerationError]);

  if (photoType === "booth") return null;

  const handlePhotoAccept = (event) => {
    const eventButtonText = event.target.innerText;
    const { imageHandshake, imageUrl } = moderatedSelfieData;
    toggleOverlay(true);

    const addPhotoPayload = {
      order_id: orderId,
      order_key: orderRef,
      photo_url: imageUrl,
      photo_handshake: imageHandshake
    };

    requestAddPhotoApi(addPhotoPayload)
      .then(() => {
        updateOrderDetailLocalStorage(orderId, { photo: imageUrl });
        setPhotoModerationStep("selfie_accepted");
        dispatch({ type: "SET_ORDER_PHOTO", payload: imageUrl });
        scrollToApp();
      })
      .catch((err) => {
        setPhotoModerationStep("selfie_review");
        setSubmitMessagePhraseKey(err.message);
      })
      .finally(() => {
        toggleOverlay(false);
      });

    triggerAnalyticsEvent("photo_upload_click", {
      chosen_photo_click: "chosen_photo_confirm",
      eventLabel: eventButtonText
    });
  };

  const handlePhotoUploadComplete = (data: PhotoUploadPayload) => {
    if (data?.imageHandshake && data?.imageUrl) {
      setPhotoModerationStep("selfie_review");
      dispatch({ type: "SET_PHOTO_MOD_DATA", payload: data });
    } else if (!data?.imageHandshake && !data?.photoModeration?.accepted) {
      setPhotoModerationStep("selfie_rejected");
    } else {
      setPhotoModerationStep("select_photo");
    }
  };

  return (
    <div className="order-confirmation__photo-upload">
      {photoModerationLoading ? (
        <PhotoLoad />
      ) : (
        <>
          <Group visible={photoType === "all"}>
            <HorizontalTextLine className="or-text">
              <Say phrase="chosen.thank_you.photo_option_or" />
            </HorizontalTextLine>
          </Group>
          <Group visible={photoType === "selfie"}>
            <OrderConfirmationSelfieOnlyText />
          </Group>
          <SelfieButton
            visible={photoType !== "booth" && photoModerationStep === "select_photo"}
            handleFileChange={(e) => handleFileChange(e, handlePhotoUploadComplete)}
            classList={`${photoType === "selfie" ? "photo-moderation__selfie-button" : "photo-moderation__selfie-button--secondary"}`}
          />
          <SelfieModerationSteps
            photoType={photoType}
            base64Photo={base64Photo}
            step={photoModerationStep}
            handlePhotoAccept={handlePhotoAccept}
            handlePhotoBoothClick={() => {}}
            handleFileChange={(e) => handleFileChange(e, handlePhotoUploadComplete)}
            isServiceError={isServiceError}
          />
          <SubmitMessage
            messagePhraseKey={submitMessagePhraseKey}
            alertType="danger"
            className=""
            visible={!!submitMessagePhraseKey}
          />
        </>
      )}
    </div>
  );
};

export default OrderConfirmationPhotoUpload;
